/**
 * How a tenant is drawn: its icon and its accent color.
 *
 * The icon comes from the tenant's own `iconName` once step 3 of the BU setup is done; the
 * defaults below only cover the four tenants that predate the field. The color is the tenant's
 * utility domain (water blue, energy yellow, and so on).
 *
 * @module Shared/brand
 */

import type { IGLIcon } from '@group-link-one/gl-icons';

/** Shown when a tenant has no icon set and is not one of the four known slugs. */
export const FALLBACK_TENANT_ICON: IGLIcon['variant'] = 'Misc.Buildings';

const ICON_BY_TENANT: Record<string, IGLIcon['variant']> = {
  'water-platform': 'GL.Water',
  'energy-platform': 'GL.Energy',
  dataoris: 'Misc.ChartLineUp',
  'sonar-print': 'Communication.Newspaper',
};

const COLOR_BY_TENANT: Record<string, string> = {
  'water-platform': 'var(--color-water)',
  'energy-platform': 'var(--gl-yellow-dark)',
  dataoris: 'var(--gl-purple)',
  'sonar-print': 'var(--color-gas)',
};

/** Every icon a tenant may be given, as the BU setup's icon picker offers them. */
export const TENANT_ICON_OPTIONS: IGLIcon['variant'][] = [
  'GL.Water',
  'GL.Energy',
  'Misc.Buildings',
  'Misc.ChartLineUp',
  'Communication.Newspaper',
  'Misc.Broadcast',
  'Menu.SquaresFour',
  'Misc.Key',
];

/**
 * Resolves a tenant's icon.
 *
 * Prefers the stored `iconName`, but only when it names a real `gl-icons` variant — a stale or
 * hand-edited value falls back rather than crashing the icon component.
 */
export function getTenantIcon(tenant: {
  slug?: string | null;
  iconName?: string | null;
}): IGLIcon['variant'] {
  const stored = tenant.iconName as IGLIcon['variant'] | undefined | null;
  if (stored && TENANT_ICON_OPTIONS.includes(stored)) return stored;

  return (tenant.slug ? ICON_BY_TENANT[tenant.slug] : undefined) ?? FALLBACK_TENANT_ICON;
}

/** Resolves a tenant's accent color, defaulting to the brand primary. */
export function getTenantColor(tenantSlug: string | null | undefined): string {
  if (!tenantSlug) return 'var(--color-primary)';
  return COLOR_BY_TENANT[tenantSlug] ?? 'var(--color-primary)';
}
